import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import translate from "@insite/client-framework/Translate";
import {
    BaseAddressModel,
    CartModel,
    CustomerValidationDto,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio, { RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps, RadioGroupStyle } from "@insite/mobius/RadioGroup";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";
import ADIAddressInfoDisplay, { ADIAddressInfoDisplayStyles } from "./ADIAddressInfoDisplay";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ADIColors from "@NA/Utils/ADIColors";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";

type BaseAddressModelWithValidation = BaseAddressModel & { validation?: CustomerValidationDto | null };

interface OwnProps {
    customer: BaseAddressModelWithValidation;
    isSelected?: boolean;
    onSelect: (customer: BaseAddressModel) => void;
    /** @deprecated Not used anymore */
    allowEditCustomer?: boolean;
    onEdit?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => void;
    extendedStyles?: ADICustomerCardStyles;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        cart,
        state,
        billTo: getBillToState(state, cart?.billToId),
    };
};

const mapDispatchToProps = {
    loadShipTos,
    loadCart,
    loadBillTo,
    updateCart,
    updateShipTo,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface ADICustomerCardStyles {
    container?: GridContainerProps;
    addressGridItem?: GridItemProps;
    address?: ADIAddressInfoDisplayStyles;
    actionsGridItem?: GridItemProps;
    actionsWrapper?: InjectableCss;
    selectButton?: ButtonPresentationProps;
    editLink?: LinkPresentationProps;
    radiogroupStyles?: RadioGroupProps;
}

export const adiCustomerCardStyles: ADICustomerCardStyles = {
    radiogroupStyles: {
        css: css`
            > ${RadioStyle} {
                input {
                    display: none;
                }
                label {
                    cursor: pointer;
                    margin-left: 0px;
                }
            }
            address {
                cursor: pointer;
                display: flex;
                flex-direction: column;
                [data-test-selector="addressInfoDisplay_nickname"] {
                    order: -1;
                    padding-top: 0px;
                    width: 100%;
                }
                [data-test-selector="addressInfoDisplay_country"] {
                    display: none;
                }
                [data-test-selector="addressInfo"] {
                    [data-test-selector="addressInfoDisplay_postalCode"] {
                        display: block;
                    }
                }
            }
        `,
    },
    container: {
        css: css`
            padding: 15px 0;
            &:first-child {
                border-top: 1px solid ${getColor("common.border")};
            }
            &:last-child {
                border-bottom: none;
                border-top: 1px solid #ececec;
            }
            > ${GridOffset} {
                width: 100% !important;
                margin-left: 0px !important;
                margin-right: 0px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            display: flex;
                            justify-content: space-between;
                            flex-wrap: nowrap;
                        `,
                        css`
                            display: flex;
                            justify-content: space-between;
                            flex-wrap: nowrap;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
        `,
    },
    addressGridItem: {
        width: 6,
        css: css`
            padding-left: 0px;
            padding-top: 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: initial;
                        max-width: initial;
                        flex-basis: auto;
                        padding-right: 0;
                    `,
                    css`
                        min-width: initial;
                        max-width: initial;
                        flex-basis: auto;
                        padding-right: 0;
                    `,
                    null,
                    null,
                    null,
                ])}
            > ${RadioGroupStyle} {
                flex-direction: row;
                > address {
                    > ${TypographyStyle} {
                        color: ${getColor("text.main")};
                    }
                }
                input:not(:disabled) + label::before {
                    border-color: ${ADIColors.gray3};
                }
                &:hover {
                    cursor: pointer;
                    input + label::after {
                        content: "";
                        display: block;
                        height: 10px;
                        width: 10px;
                        border-radius: 50%;
                        background: ${getColor("primary")};
                        position: absolute;
                        left: 4px;
                        top: calc(50% - 5px);
                    }
                    input:not(:disabled) + label::before {
                        border-color: ${ADIColors.blue5};
                        background: ${ADIColors.white};
                    }
                }
            }
        `,
    },
    actionsGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
            padding-right: 0px;
            padding-top: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: fit-content;
                        max-width: initial;
                        flex-basis: auto;
                    `,
                    css`
                        min-width: fit-content;
                        max-width: initial;
                        flex-basis: auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            > ${StyledButton} {
                margin-right: 8px;
                margin-top: 18px;
                > ${TypographyStyle} {
                    font-size: 14px;
                    font-weight: bold;
                }
                &:last-child {
                    margin-right: 0px;
                    &:focus {
                        margin-right: 2px;
                    }
                }
            }
        `,
    },
    selectButton: { variant: "tertiary" },
    editLink: {
        css: css`
            margin-top: 1rem;
            &:hover {
                ${TypographyStyle} {
                    color: ${getColor("primary")};
                }
            }
            &:focus {
                margin-right: 8px;
            }
            span {
                &:hover {
                    text-decoration: underline;
                    color: #cccccc;
                }
            }
            &:disabled {
                span {
                    color: #cccccc;
                    &:hover {
                        color: #cccccc;
                    }
                }
            }
        `,
    },
};

const ActionsWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const ADICustomerCard = ({
    customer,
    toaster,
    onEdit,
    extendedStyles,
    onSelect,
    cart,
    billTo,
    updateShipTo,
}: Props) => {
    const [styles] = useState(() => mergeToNew(adiCustomerCardStyles, extendedStyles));
    const [isLoadingRemove, setIsLoadingRemove] = useState(false);

    const handleEditClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => onEdit?.(event, customer);

    const handleRemoveClick = (event: any) => {
        event.preventDefault();
        if (cart?.shipToId !== customer.id) {
            setIsLoadingRemove(true);
            const shipToClone = cloneDeep(customer as ShipToModel);
            shipToClone.properties["removeinfuture"] = "Y";
            shipToClone.properties["selectasalternate"] = "";
            shipToClone.properties["isactive"] = "0";
            shipToClone.properties["isLocalDeleted"] = "true";
            updateShipTo({
                billToId: billTo.id!,
                shipTo: shipToClone,
                properties: {
                    notLoadSession: true,
                    skipUpdateCart: true,
                },
                isEdit: true,
                onSuccess: () => {
                    toaster.addToast({ body: "Address has been removed.", messageType: "success" });
                    setIsLoadingRemove(false);
                },
            } as {
                billToId: string;
                shipTo: ShipToModel;
                properties: any;
                isEdit: boolean;
            } & HasOnSuccess<boolean>);
        }
    };

    return (
        <LoadingOverlay loading={isLoadingRemove}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.addressGridItem}>
                    <RadioGroup value={cart?.shipToId} onClick={() => onSelect(customer)} {...styles.radiogroupStyles}>
                        <Radio key="Alternate Ship" value={customer.id}></Radio>
                        <ADIAddressInfoDisplay
                            {...customer}
                            state={customer.state?.abbreviation}
                            country={customer.country?.abbreviation}
                            extendedStyles={styles.address}
                        />
                    </RadioGroup>
                </GridItem>
                <GridItem {...styles.actionsGridItem}>
                    <ActionsWrapper {...styles.actionsWrapper}>
                        {onEdit && (
                            <Link
                                {...styles.editLink}
                                onClick={handleEditClick}
                                data-test-selector={`customerCard_edit_${customer.id}`}
                            >
                                {translate("Edit")}
                            </Link>
                        )}
                        <Link
                            {...styles.editLink}
                            onClick={handleRemoveClick}
                            data-test-selector={`customerCard_remove_${customer.id}`}
                            disabled = {cart?.shipToId == customer.id}
                        >
                            {translate("Remove")}
                        </Link>
                    </ActionsWrapper>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(ADICustomerCard));
